import React, { useState, useEffect, useMemo, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarAlt,
  faLocationDot,
  faRunning,
  faChalkboardTeacher,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { db } from "@lib/firebase";
import { collection, getDocs } from "firebase/firestore";
import { InputField } from "@cakrawalajakarta/shared-components";

const formatDate = (dates) => {
  if (!dates || !Array.isArray(dates) || dates.length === 0) {
    return "Tanggal tidak tersedia";
  }
  try {
    const validDates = dates
      .filter((date) => /^\d{4}-\d{2}-\d{2}$/.test(date))
      .map((date) => new Date(date))
      .filter((date) => !isNaN(date.getTime()));

    if (validDates.length === 0) {
      return "Tanggal tidak tersedia";
    }

    validDates.sort((a, b) => a - b);

    const formattedDates = validDates.map((date) => ({
      day: date.toLocaleDateString("id-ID", { day: "numeric" }),
      month: date.toLocaleDateString("id-ID", { month: "long" }),
      year: date.toLocaleDateString("id-ID", { year: "numeric" }),
    }));

    const uniqueDates = [];
    const seen = new Set();
    for (const date of formattedDates) {
      const dateString = `${date.day} ${date.month} ${date.year}`;
      if (!seen.has(dateString)) {
        seen.add(dateString);
        uniqueDates.push(date);
      }
    }

    if (uniqueDates.length === 1) {
      return `${uniqueDates[0].day} ${uniqueDates[0].month} ${uniqueDates[0].year}`;
    }

    return `${uniqueDates[0].day} - ${
      uniqueDates[uniqueDates.length - 1].day
    } ${uniqueDates[uniqueDates.length - 1].month} ${
      uniqueDates[uniqueDates.length - 1].year
    }`;
  } catch {
    return "Tanggal tidak tersedia";
  }
};

const SkeletonLoader = () => (
  <div className="grid md:grid-cols-3 gap-4">
    {[...Array(3)].map((_, index) => (
      <div key={index} className="bg-white rounded-2xl border border-slate-200">
        <div className="w-full h-60 bg-gray-200 animate-pulse"></div>
        <div className="p-4 space-y-4">
          <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
          <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
          <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
        </div>
      </div>
    ))}
  </div>
);

const EventCard = React.memo(({ event, index }) => {
  const getEventIcon = (type) => {
    switch (type.toLowerCase()) {
      case "trail_running":
        return { icon: faRunning, size: "text-5xl" };
      case "seminar":
        return { icon: faChalkboardTeacher, size: "text-4xl" };
      default:
        return { icon: faCalendarAlt, size: "text-4xl" };
    }
  };

  const { icon, size } = getEventIcon(event.type);

  return (
    <motion.div
      className="bg-white rounded-2xl border border-slate-200 flex flex-col text-left relative overflow-hidden"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.2, ease: "easeInOut" }}
      viewport={{ once: true }}
    >
      <div className="relative w-full h-60">
        <img
          src={event.image || "https://via.placeholder.com/800x400"}
          alt={event.title}
          className="w-full h-60 object-cover rounded-t-2xl"
          loading="lazy"
        />
        <div className="absolute inset-0 rounded-t-2xl flex items-center justify-center">
          <FontAwesomeIcon
            icon={icon}
            className={`text-white ${size} z-10`}
            aria-label={`Ikon untuk ${
              event.type === "trail_running" ? "Trail Running" : "Seminar"
            }`}
          />
        </div>
      </div>
      <div className="px-4 flex flex-col flex-grow text-cyan-900">
        <motion.h3
          className="text-xl font-black mt-4 text-cyan-600 uppercase"
          initial={{ opacity: 0, y: -15 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.5,
            delay: index * 0.4,
            ease: "easeInOut",
          }}
          viewport={{ once: true }}
        >
          {event.title}
        </motion.h3>
        <motion.p
          className="flex-grow mt-4"
          initial={{ opacity: 0, y: -15 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: index * 0.5, ease: "easeInOut" }}
          viewport={{ once: true }}
        >
          {event.aboutEvent && event.aboutEvent.length > 150
            ? event.aboutEvent.substring(0, 150) + "..."
            : event.aboutEvent || "Deskripsi tidak tersedia"}
        </motion.p>
        <ul className="text-left mt-4">
          <motion.li
            className="flex items-center mb-2"
            initial={{ opacity: 0, y: -15 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.5,
              delay: index * 0.5,
              ease: "easeInOut",
            }}
            viewport={{ once: true }}
          >
            <FontAwesomeIcon
              icon={faCalendarAlt}
              className="mr-2 text-cyan-600"
            />
            <span>Tanggal: {formatDate(event.dates)}</span>
          </motion.li>
          <motion.li
            className="flex items-center mb-2"
            initial={{ opacity: 0, y: -15 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.5,
              delay: index * 0.6,
              ease: "easeInOut",
            }}
            viewport={{ once: true }}
          >
            <FontAwesomeIcon
              icon={faLocationDot}
              className="mr-2 text-cyan-600"
            />
            <span>{event.location || "Lokasi tidak tersedia"}</span>
          </motion.li>
          <motion.li
            className="flex items-center mb-2"
            initial={{ opacity: 0, y: -15 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.5,
              delay: index * 0.7,
              ease: "easeInOut",
            }}
            viewport={{ once: true }}
          >
            <div
              className={`w-2.5 h-2.5 rounded-full mr-2 ${
                event.status === "Registrasi Dibuka"
                  ? "bg-green-500"
                  : event.status === "Registrasi Ditutup"
                  ? "bg-red-500"
                  : "bg-yellow-500"
              }`}
            ></div>
            <span>{event.status}</span>
          </motion.li>
        </ul>
        <motion.div
          className="text-center mt-auto"
          initial={{ opacity: 0, y: -15 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: index * 0.8, ease: "easeInOut" }}
          viewport={{ once: true }}
        >
          <Link to={`/kegiatan/${event.slug}`}>
            <button className="w-full py-3 border-2 border-cyan-600 hover:bg-cyan-600 hover:text-white text-base font-semibold text-cyan-600 rounded-xl my-4">
              Lihat Detail
            </button>
          </Link>
        </motion.div>
      </div>
    </motion.div>
  );
});

export const Kegiatan = React.memo(() => {
  const [events, setEvents] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchEvents = useCallback(async () => {
    try {
      if (!db || typeof db !== "object" || !("app" in db)) {
        throw new Error("Firestore db is not initialized or invalid");
      }

      if (typeof collection !== "function") {
        throw new Error("Firestore collection function is not available");
      }

      const snapshot = await getDocs(collection(db, "semuaKegiatan"));
      const activities = snapshot.docs.map((doc) => {
        const data = doc.data();
        const dates =
          data.categories && data.categories.length > 0
            ? [
                ...new Set(data.categories.map((category) => category.date)),
              ].filter((date) => date)
            : [];
        return {
          id: doc.id,
          title: data.title || "Judul tidak tersedia",
          image: data.image || "",
          location: data.location || "Lokasi tidak tersedia",
          status: data.status || "Status tidak tersedia",
          slug: data.slug || "",
          aboutEvent: data.aboutEvent || "Deskripsi tidak tersedia",
          dates: dates,
          eventDateTimestamp:
            data.categories && data.categories.length > 0
              ? data.categories[0].eventDateTimestamp || new Date(0)
              : new Date(0),
          type: data.type || "unknown",
        };
      });

      const sortedActivities = activities
        .filter((event) => event.id !== "JOTR" && event.id !== "FUNRUN5K2026" && event.id !== "ROADTOJOTR2026" && event.id !== "ULTRA24") // Hide JOTR 2025, Fun Run 5K Cibodas, Road to JOTR 2026, and Indonesia Ultra 24
        .sort((a, b) => {
        const dateA =
          a.eventDateTimestamp instanceof Date
            ? a.eventDateTimestamp
            : new Date(a.eventDateTimestamp || 0);
        const dateB =
          b.eventDateTimestamp instanceof Date
            ? b.eventDateTimestamp
            : new Date(b.eventDateTimestamp || 0);
        return dateA - dateB;
      });

      setEvents(sortedActivities);
      setIsLoaded(true);
    } catch (err) {
      setError("Gagal memuat kegiatan: " + err.message);
      setIsLoaded(true);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    fetchEvents();
    return () => {
      mounted = false;
    };
  }, [fetchEvents]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sortedActivities = useMemo(() => events, [events]);

  const filteredActivities = useMemo(
    () =>
      sortedActivities.filter((event) =>
        event.title.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [sortedActivities, searchTerm]
  );

  if (!isLoaded) {
    return (
      <section className="w-full min-h-screen py-20 text-center text-cyan-600 flex-col justify-center relative bg-slate-100">
        <div className="relative z-1 container mx-auto px-4">
          <SkeletonLoader />
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="w-full min-h-screen py-20 text-center text-cyan-600 flex-col justify-center relative bg-slate-100">
        <div className="relative z-1 container mx-auto px-4">
          <p className="text-red-500">{error}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="w-full min-h-screen py-20 text-center text-cyan-600 flex-col justify-center relative bg-slate-100">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            className="mb-6 flex flex-col gap-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            viewport={{ once: true }}
          >
            <div className="flex flex-col md:flex-row-reverse md:items-center md:justify-between gap-4">
              <h3 className="text-lg md:text-xl font-black uppercase text-cyan-600 md:text-right">
                AGENDA KEGIATAN
              </h3>
              <div className="w-full sm:max-w-xl md:ml-0">
                <InputField
                  name="search"
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Cari kegiatan..."
                  aria-label="Cari kegiatan berdasarkan judul"
                  labelColor="text-slate-800"
                  style={{ fontSize: "0.875rem" }}
                  startIcon={<FontAwesomeIcon icon={faSearch} />}
                />
              </div>
            </div>
          </motion.div>
          {filteredActivities.length === 0 ? (
            <p className="text-cyan-600">
              Tidak ada kegiatan yang tersedia saat ini.
            </p>
          ) : (
            <motion.div className="grid md:grid-cols-3 gap-4 z-1 relative">
              {filteredActivities.map((event, index) => (
                <EventCard key={event.id} event={event} index={index} />
              ))}
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
});
